import { Card } from "@/components/atoms/card";
import { Button } from "@/components/atoms/button";
import { User, Pencil } from "lucide-react";
import ProfileImageUpload from "./ProfileImageUpload";
import ProfileForm from "./ProfileForm";
import { useProfileEdit } from "@/hooks/useProfileEdit";
import { useAuth } from "@/hooks/useAuth";

interface ProfileInfoSectionProps {
  userData: any;
  user: any;
  onDataUpdated: () => void;
}

export default function ProfileInfoSection({
  userData,
  user,
  onDataUpdated,
}: ProfileInfoSectionProps) {
  const profileEdit = useProfileEdit({ userData, onDataUpdated });
  const { userRole } = useAuth();
  return (
    <>
      {/* Card 1: Título Información personal */}
      <Card className="bg-white p-3 sm:p-4 md:p-6 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <h2 className="text-sm sm:text-base font-semibold">
                Información personal
              </h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-500">
              Información básica de tu perfil
            </p>
          </div>
          {(userRole === "admin" || userRole === "manager") && (
            <div className="flex-shrink-0">
              {!profileEdit.isEditMode ? (
                <Button
                  onClick={profileEdit.handleEditMode}
                  size="sm"
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto text-xs sm:text-sm px-3 py-2"
                >
                  <Pencil className="w-3 h-3 sm:w-4 sm:h-4" />
                  Editar
                </Button>
              ) : (
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    onClick={profileEdit.handleCancelEdit}
                    variant="outline"
                    size="sm"
                    className="border-gray-300 text-gray-600 hover:bg-gray-50 flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-3 py-2"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={profileEdit.handleSaveEdit}
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-3 py-2"
                    disabled={!profileEdit.canSave() || profileEdit.isSaving}
                  >
                    {profileEdit.isSaving ? "Guardando..." : "Guardar"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Card 2: Inputs con datos de usuario e imagen */}
      <Card className="bg-white p-3 sm:p-4 md:p-6 mb-10">
        <div>
          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 items-center lg:items-center min-h-[160px]">
            {/* Avatar y cambiar foto */}
            <ProfileImageUpload
              userData={userData}
              user={user}
              isEditMode={profileEdit.isEditMode}
              newProfileImage={profileEdit.newProfileImage}
              fileError={profileEdit.fileError}
              fileInputRef={profileEdit.fileInputRef}
              onPhotoChange={profileEdit.handlePhotoChange}
              onRemovePhoto={profileEdit.handleRemovePhoto}
            />

            {/* Formulario info */}
            <ProfileForm
              userData={userData}
              isEditMode={profileEdit.isEditMode}
              editedName={profileEdit.editedName}
              nameError={profileEdit.nameError}
              onNameChange={profileEdit.handleNameChange}
              onNameBlur={profileEdit.handleNameBlur}
            />
          </div>
        </div>
      </Card>
    </>
  );
}
